// @ts-nocheck
/**
 * Balance-sheet result card: the BS analog of FinalMappingCard, scoped to
 * what the BS backend produces today. It renders the classified accounts
 * table with editable category dropdowns (saved via the same mapping PATCH
 * as the P&L — the server re-runs the A = L + E balance check and returns
 * the recomputed mapping), plus the per-period balance check and the Excel
 * download. No P&L-only tabs (Trends / Insights / EBITDA Bridge). Reuses
 * the fmc-* visual language from Agent.css.
 */
import React, { useMemo, useState } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Download,
  Loader2,
  FileSpreadsheet,
  Save,
} from 'lucide-react';

import { downloadAgentMappingAsExcel, SourceFileMissingError } from '../../utils/exportExcel';
import { patchAgentMapping } from '../../utils/agentApi';
import CellReferencesToggle from './CellReferencesToggle';
import { effectiveBsStatus, periodTiesToSource } from './bsBalanceStatus';
import { useTaxonomy } from './taxonomyContext';
import './Agent.css';

const fmtCell = (n) => {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return n < 0 ? `(${formatted})` : formatted;
};

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
};

// value_by_period is a positional array aligned to periods[] (new backend
// shape) or a legacy int/str-keyed dict.
const periodKey = (acct, pidx, pos) => {
  const vbp = acct.value_by_period;
  if (Array.isArray(vbp)) return (pos == null ? null : vbp[pos]) ?? null;
  return vbp?.[pidx] ?? vbp?.[String(pidx)] ?? null;
};

const STATUS_META = {
  balanced: { label: 'Balanced', tone: 'good', icon: CheckCircle },
  // Mapping ties to the source's stated section totals; the residual gap is
  // the source workbook's own imbalance, not a mapping error.
  source_unbalanced: { label: 'Matches source', tone: 'good', icon: CheckCircle },
  off: { label: 'Off', tone: 'warn', icon: AlertTriangle },
  // TB->BS vocabulary; plain BS never emits it, but the card is shared shape.
  mapping_error: { label: 'Mapping error', tone: 'bad', icon: XCircle },
  error: { label: 'Error', tone: 'bad', icon: XCircle },
  unknown: { label: 'No check', tone: 'muted', icon: AlertTriangle },
};

// `embedded` renders the card headerless for composition inside the unified
// mega-card (which owns the header and tabs). Table, category edits + save,
// balance check, and the value/reference export all behave identically.
const FinalMappingCardBS = ({ mapping, sessionId, onMappingUpdated, embedded = false }) => {
  // Selectable buckets, grouping, ordering and gradients. The standard
  // balance-sheet constants unless a host mounted a TaxonomyProvider (Databook).
  const { bs: taxonomy } = useTaxonomy();
  const periods = mapping?.periods || [];
  const accounts = mapping?.accounts || [];
  const periodBreakdowns = mapping?.period_breakdowns || [];
  const status = effectiveBsStatus(mapping);
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const StatusIcon = meta.icon;

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  // Default ON, matching the P&L mapper: the downloaded .xlsx embeds the source
  // sheet and links each populated cell back to it. Flips off automatically if
  // the source file has been cleaned out of /tmp (see handleDownload).
  const [enableReferencing, setEnableReferencing] = useState(true);
  // Local category overrides keyed by row_index, mirroring FinalMappingCard:
  // ``handleSave`` PATCHes them to the backend, which re-runs the A = L + E
  // balance check from the persisted values and returns the recomputed
  // mapping; the caller swaps it into chat items via ``onMappingUpdated``.
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const hasEdits = Object.keys(edits).length > 0;

  // Group accounts by bucket, ordered by the balance-sheet bucket order.
  const groups = useMemo(() => {
    const byCat = {};
    for (const a of accounts) {
      const cat = taxonomy.groupOf(a.category || 'Uncategorized');
      (byCat[cat] = byCat[cat] || []).push(a);
    }
    const idx = (c) => {
      const i = taxonomy.order.indexOf(c);
      return i === -1 ? 999 : i;
    };
    return Object.keys(byCat)
      .sort((a, b) => idx(a) - idx(b) || a.localeCompare(b))
      .map((cat) => ({ category: cat, accounts: byCat[cat] }));
  }, [accounts, taxonomy]);

  const subtotals = useMemo(
    () =>
      groups.map((g) => {
        const out = {};
        periods.forEach((p, pos) => {
          let s = 0;
          for (const a of g.accounts) {
            const v = periodKey(a, p.period_index, pos);
            if (typeof v === 'number') s += v;
          }
          out[p.period_index] = s;
        });
        return out;
      }),
    [groups, periods],
  );

  // Pull mapped Assets/Liabilities/Equity out of a period breakdown's
  // section_rollups (BS rollup shape: { section, mapped, anchor, tie_out }).
  const sectionRollup = (pb) => {
    const out = { assets: null, liabilities: null, equity: null };
    for (const r of pb?.section_rollups || []) {
      if (r && Object.prototype.hasOwnProperty.call(out, r.section)) {
        out[r.section] = r.mapped;
      }
    }
    return out;
  };

  const handleSave = async () => {
    if (!sessionId || !hasEdits) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await patchAgentMapping(sessionId, edits);
      onMappingUpdated?.(updated);
      setEdits({});
    } catch (err) {
      // Keep edits intact so the user can retry.
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!sessionId) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const idxs = periods.map((p) => p.period_index);
      await downloadAgentMappingAsExcel(sessionId, mapping, idxs, enableReferencing);
    } catch (e) {
      if (e instanceof SourceFileMissingError) {
        // Flip the toggle off so a second click succeeds without re-failing.
        setEnableReferencing(false);
        setDownloadError(
          'Source file no longer available — cell references turned off. Click Download again to retry without them.',
        );
      } else {
        setDownloadError(e?.message || String(e));
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={`fmc-card ${embedded ? 'fmc-card--embedded' : ''}`}>
      {!embedded && (
      <div className="fmc-header">
        <div className="fmc-header-left">
          <FileSpreadsheet size={14} />
          <span className="fmc-filename">
            {mapping?.file?.filename || 'Balance Sheet'}
          </span>
        </div>
        <div className="fmc-header-right">
          <div className={`fmc-status fmc-status-${meta.tone}`}>
            <StatusIcon size={12} />
            <span>{meta.label}</span>
          </div>
        </div>
      </div>
      )}

      {!['balanced', 'source_unbalanced'].includes(status) && (
        <div className="result-notice warn">
          {status === 'unknown'
            ? 'The balance check never ran for this mapping. Review before relying on these figures.'
            : `This mapping did not reconcile to Assets = Liabilities + Equity (status ${status}${
                typeof mapping?.reconciliation?.final_discrepancy === 'number' &&
                mapping.reconciliation.final_discrepancy
                  ? `, worst-period gap ${fmtMoney(mapping.reconciliation.final_discrepancy)}`
                  : ''
              }). Review before relying on these figures.`}
        </div>
      )}

      <div className="fmc-table-wrap">
        <table className="fmc-table">
          <thead>
            <tr>
              <th className="fmc-col-account">Account</th>
              <th className="fmc-col-category">Category</th>
              {periods.map((p) => (
                <th key={p.period_index} className="fmc-col-period">
                  {p.display_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => (
              <React.Fragment key={g.category}>
                <tr className="fmc-group-row">
                  <td className="fmc-group-name" colSpan={2}>
                    {g.category}
                    <span className="fmc-group-count">
                      ({g.accounts.length})
                    </span>
                  </td>
                  {periods.map((p) => (
                    <td key={p.period_index} className="fmc-group-total fmc-num">
                      {fmtCell(subtotals[gi][p.period_index])}
                    </td>
                  ))}
                </tr>
                {g.accounts.map((acct) => {
                  const editedCat = edits[acct.row_index];
                  const currentCat = editedCat ?? acct.category ?? 'Uncategorized';
                  const isEdited = editedCat != null;
                  return (
                  <tr
                    key={acct.row_index}
                    className={`fmc-row ${acct.sign_flipped ? 'fmc-sign-flipped' : ''} ${isEdited ? 'fmc-edited' : ''}`}
                  >
                    <td className="fmc-account" title={acct.account_name || ''}>
                      <div className="fmc-account-name">
                        <span className="fmc-account-text">
                          {acct.account_name || (
                            <span className="fmc-muted">—</span>
                          )}
                        </span>
                        {acct.sign_flipped && (
                          <span
                            className="fmc-sign-flipped-tag"
                            title={`Source value was negative; mapped as a positive magnitude (sign swapped).`}
                          >
                            sign flipped
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="fmc-category">
                      <select
                        className={`fmc-cat-combo ${isEdited ? 'edited' : ''}`}
                        value={currentCat}
                        onChange={(e) => {
                          const next = e.target.value;
                          setEdits((prev) => {
                            const out = { ...prev };
                            if (next === (acct.category ?? 'Uncategorized')) {
                              delete out[acct.row_index];
                            } else {
                              out[acct.row_index] = next;
                            }
                            return out;
                          });
                        }}
                        style={taxonomy.style(currentCat)}
                        title={`Change category from ${currentCat}`}
                      >
                        {!taxonomy.selectable.includes(currentCat) && (
                          <option value={currentCat}>{currentCat}</option>
                        )}
                        {taxonomy.selectable.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </td>
                    {periods.map((p, pos) => {
                      const v = periodKey(acct, p.period_index, pos);
                      return (
                        <td
                          key={p.period_index}
                          className={`fmc-num ${v < 0 ? 'fmc-neg' : ''}`}
                        >
                          {fmtCell(v)}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {periodBreakdowns.length > 0 && (
        <div className="fmc-recon-wrap">
          <div className="fmc-recon-scroll">
            <table className="fmc-recon-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="fmc-num">Assets</th>
                  <th className="fmc-num">Liabilities</th>
                  <th className="fmc-num">Equity</th>
                  <th className="fmc-num">Gap</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {periodBreakdowns.map((pb, idx) => {
                  const r = sectionRollup(pb);
                  const passes = pb.passes !== false;
                  // Off to zero but tied to the source's stated section
                  // totals: the gap is the source's own imbalance, so this
                  // period is not a mapping failure.
                  const tiesToSource = !passes && periodTiesToSource(pb);
                  return (
                    <tr key={pb.period_index ?? idx}>
                      <td>{pb.period_display || `Period ${pb.period_index}`}</td>
                      <td className="fmc-num">{fmtMoney(r.assets)}</td>
                      <td className="fmc-num">{fmtMoney(r.liabilities)}</td>
                      <td className="fmc-num">{fmtMoney(r.equity)}</td>
                      <td
                        className={`fmc-num ${passes || tiesToSource ? '' : 'fmc-neg'}`}
                      >
                        {fmtMoney(pb.gap)}
                      </td>
                      <td>
                        <span
                          className={`fmc-recon-badge ${
                            passes || tiesToSource ? 'pass' : 'fail'
                          }`}
                          title={
                            tiesToSource
                              ? 'Mapped sections tie to the source totals; the gap is the source sheet’s own imbalance.'
                              : undefined
                          }
                        >
                          {passes
                            ? 'BALANCED'
                            : tiesToSource
                              ? 'MATCHES SOURCE'
                              : 'OFF'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category-edit save row — mirrors FinalMappingCard. Rendered in both
          standalone and embedded modes; the PATCH goes to this statement's
          own session id either way. */}
      <div className="fmc-actions fmc-actions-lg">
        <button
          type="button"
          className="fmc-btn fmc-btn-lg fmc-btn-primary"
          onClick={handleSave}
          disabled={!hasEdits || saving || !sessionId}
        >
          <Save size={14} />
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {hasEdits && !saving && (
          <span className="fmc-action-hint">
            Unsaved edits — save first to include them in the export.
          </span>
        )}
        {saveError && (
          <span className="fmc-action-error" title={saveError}>
            {saveError}
          </span>
        )}
      </div>

      {/* Embedded in the unified mega-card the host header owns the single
          export (toggle included) — the per-statement download block would
          be a second path to the same file. */}
      {!embedded && (
      <div className="fmc-actions fmc-actions-lg fmc-actions-stack">
        <CellReferencesToggle
          checked={enableReferencing}
          onChange={setEnableReferencing}
          disabled={downloading}
          hint="When on, the downloaded .xlsx includes a copy of the source sheet and links populated cells back to it via formulas."
        />
        <div className="fmc-export-note">
          {enableReferencing
            ? 'Each formula is checked against the source cell. The file includes a Tie-Out sheet that re-verifies itself in Excel.'
            : 'Exporting without cell links — values only.'}
        </div>
        <button
          type="button"
          className={`fmc-btn fmc-btn-lg fmc-btn-primary ${downloading ? 'fmc-btn-loading' : ''}`}
          onClick={handleDownload}
          disabled={downloading || !sessionId || periods.length === 0}
        >
          {downloading ? (
            <Loader2 size={14} className="fmc-spin" />
          ) : (
            <Download size={14} />
          )}
          <span className="fmc-btn-label">
            {downloading
              ? 'Generating…'
              : `Download .xlsx · ${periods.length} ${
                  periods.length === 1 ? 'period' : 'periods'
                }`}
          </span>
        </button>
        {downloadError && (
          <span className="fmc-action-error" title={downloadError}>
            {downloadError}
          </span>
        )}
      </div>
      )}
    </div>
  );
};

export default FinalMappingCardBS;
